import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Star, Send, Trash2, Users, MessageSquare, Check } from "lucide-react";

export default function AdminReviews() {
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [customersForReview, setCustomersForReview] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("customers"); // customers, invitations, reviews

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [customersRes, invitationsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/admin/customers-for-review`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/review-invitations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reviews?limit=50`)
      ]);
      setCustomersForReview(customersRes.data);
      setInvitations(invitationsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (customerId) => {
    try {
      await axios.post(
        `${API}/admin/review-invitations`,
        { customer_id: customerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Davet gönderildi");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Davet gönderilemedi");
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    try {
      await axios.delete(`${API}/admin/review-invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Davet silindi");
      fetchData();
    } catch (error) {
      toast.error("Davet silinemedi");
    }
  };

  return (
    <AdminLayout title="Yorum Yönetimi">
      <div data-testid="admin-reviews">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("customers")}
            className={`px-4 py-2 rounded-lg font-accent text-sm ${
              activeTab === "customers" 
                ? "bg-[#c9a96e] text-white" 
                : "bg-white text-[#6b7280] border border-[#e5e7eb]"
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Müşteriler
          </button>
          <button
            onClick={() => setActiveTab("invitations")}
            className={`px-4 py-2 rounded-lg font-accent text-sm ${
              activeTab === "invitations" 
                ? "bg-[#c9a96e] text-white" 
                : "bg-white text-[#6b7280] border border-[#e5e7eb]"
            }`}
          >
            <Send className="w-4 h-4 inline mr-2" />
            Davetler ({invitations.filter(i => !i.has_reviewed).length})
          </button>
          <button
            onClick={() => setActiveTab("reviews")}
            className={`px-4 py-2 rounded-lg font-accent text-sm ${
              activeTab === "reviews" 
                ? "bg-[#c9a96e] text-white" 
                : "bg-white text-[#6b7280] border border-[#e5e7eb]"
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Yorumlar ({reviews.length})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Customers Tab - Select customers to invite for review */}
            {activeTab === "customers" && (
              <div className="bg-white border border-[#e5e7eb] rounded-xl shadow-sm">
                <div className="p-4 border-b border-[#e5e7eb]">
                  <h3 className="font-display text-lg text-[#1a1a2e]">Yorum Davet Edilebilecek Müşteriler</h3>
                  <p className="text-[#6b7280] text-sm">Randevusunu tamamlamış müşterileri yorum yazmak için davet edebilirsiniz.</p>
                </div>
                
                {customersForReview.length === 0 ? (
                  <div className="p-8 text-center text-[#6b7280]">
                    Henüz tamamlanmış randevusu olan müşteri yok
                  </div>
                ) : (
                  <table className="table-elite">
                    <thead>
                      <tr>
                        <th>Müşteri</th>
                        <th>Telefon</th>
                        <th>Tamamlanan Randevu</th>
                        <th>Durum</th>
                        <th>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customersForReview.map((customer) => (
                        <tr key={customer.id}>
                          <td className="text-[#1a1a2e]">{customer.name}</td>
                          <td className="text-[#6b7280]">{customer.phone}</td>
                          <td className="text-[#1a1a2e]">{customer.completed_appointments}</td>
                          <td>
                            {customer.is_invited ? (
                              <span className="status-badge status-approved">Davet Edildi</span>
                            ) : (
                              <span className="status-badge status-pending">Davet Edilmedi</span>
                            )}
                          </td>
                          <td>
                            {!customer.is_invited && (
                              <Button
                                onClick={() => handleInvite(customer.id)}
                                className="btn-primary text-xs py-1 px-3"
                                data-testid={`invite-${customer.id}`}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Davet Et
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Invitations Tab */}
            {activeTab === "invitations" && (
              <div className="bg-white border border-[#e5e7eb] rounded-xl shadow-sm">
                <div className="p-4 border-b border-[#e5e7eb]">
                  <h3 className="font-display text-lg text-[#1a1a2e]">Gönderilen Davetler</h3>
                </div>
                
                {invitations.length === 0 ? (
                  <div className="p-8 text-center text-[#6b7280]">
                    Henüz davet gönderilmemiş
                  </div>
                ) : (
                  <table className="table-elite">
                    <thead>
                      <tr>
                        <th>Müşteri</th>
                        <th>Telefon</th>
                        <th>Davet Tarihi</th>
                        <th>Durum</th>
                        <th>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.map((inv) => (
                        <tr key={inv.id}>
                          <td className="text-[#1a1a2e]">{inv.customer_name}</td>
                          <td className="text-[#6b7280]">{inv.customer_phone}</td>
                          <td className="text-[#6b7280]">
                            {format(new Date(inv.invited_at), "d MMM yyyy HH:mm", { locale: tr })}
                          </td>
                          <td>
                            {inv.has_reviewed ? (
                              <span className="status-badge status-completed">
                                <Check className="w-3 h-3 inline mr-1" />
                                Yorum Yazıldı
                              </span>
                            ) : (
                              <span className="status-badge status-pending">Bekliyor</span>
                            )}
                          </td>
                          <td>
                            {!inv.has_reviewed && (
                              <Button
                                onClick={() => handleDeleteInvitation(inv.id)}
                                variant="ghost"
                                className="text-red-600 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === "reviews" && (
              <div className="space-y-4">
                {reviews.length === 0 ? (
                  <div className="bg-white border border-[#e5e7eb] rounded-xl shadow-sm p-8 text-center text-[#6b7280]">
                    Henüz yorum yok
                  </div>
                ) : (
                  reviews.map((review) => (
                    <div 
                      key={review.id}
                      className="bg-white border border-[#e5e7eb] rounded-xl shadow-sm p-4"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-[#1a1a2e] font-medium">{review.customer_name}</p>
                          <p className="text-[#9ca3af] text-sm">
                            {format(new Date(review.created_at), "d MMM yyyy", { locale: tr })}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${star <= review.rating ? "text-[#c9a96e] fill-[#c9a96e]" : "text-[#9ca3af]"}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-[#6b7280] italic">"{review.comment}"</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
